import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus } from "lucide-react";
import { RecordContributionDialog } from "@/components/collective/RecordContributionDialog";

export default function Contributions() {
  const navigate = useNavigate();
  const [showDialog, setShowDialog] = useState(false);

  const { data: contributions, isLoading } = useQuery({
    queryKey: ["collective-contributions"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data: orgMember } = await supabase
        .from("organization_members")
        .select("organization_id")
        .eq("user_id", user.id)
        .single();

      if (!orgMember) throw new Error("No organization found");

      const { data, error } = await supabase
        .from("collective_contributions")
        .select(`
          *,
          collective_donors (
            name,
            email
          ),
          campaigns (
            name
          ),
          athletes (
            name,
            sport
          ),
          collective_contribution_allocations (
            amount_allocated
          )
        `)
        .eq("organization_id", orgMember.organization_id)
        .order("contribution_date", { ascending: false });

      if (error) throw error;

      return data?.map(contribution => {
        const totalAllocated = contribution.collective_contribution_allocations?.reduce(
          (sum, allocation) => sum + allocation.amount_allocated,
          0
        ) || 0;
        return {
          ...contribution,
          available_amount: contribution.amount - totalAllocated,
        };
      });
    },
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const formatDate = (date: string) => {
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(new Date(date));
  };

  const totalContributions = contributions?.reduce((sum, c) => sum + c.amount, 0) || 0;
  const totalAllocated = contributions?.reduce((sum, c) => sum + (c.amount - c.available_amount), 0) || 0;
  const totalAvailable = contributions?.reduce((sum, c) => sum + c.available_amount, 0) || 0;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Contribution Timeline</h1>
            <p className="text-muted-foreground">Track all donations and allocations</p>
          </div>
          <Button onClick={() => setShowDialog(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Record Contribution
          </Button>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total Received
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatCurrency(totalContributions)}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Allocated to Deals
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatCurrency(totalAllocated)}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Available to Allocate
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                {formatCurrency(totalAvailable)}
              </div>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>All Contributions</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-8 text-muted-foreground">Loading contributions...</div>
            ) : contributions?.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No contributions recorded yet
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Donor</TableHead>
                    <TableHead>Campaign/Athlete</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="text-right">Available</TableHead>
                    <TableHead>Payment Method</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {contributions?.map((contribution) => (
                    <TableRow
                      key={contribution.id}
                      className="cursor-pointer"
                      onClick={() => navigate(`/collective/donors/${contribution.donor_id}`)}
                    >
                      <TableCell>{formatDate(contribution.contribution_date)}</TableCell>
                      <TableCell className="font-medium">
                        {contribution.collective_donors?.name}
                      </TableCell>
                      <TableCell>
                        {contribution.campaigns?.name || contribution.athletes?.name || "—"}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(contribution.amount)}
                      </TableCell>
                      <TableCell className="text-right">
                        <span className={contribution.available_amount > 0 ? "text-green-600 dark:text-green-400" : ""}>
                          {formatCurrency(contribution.available_amount)}
                        </span>
                      </TableCell>
                      <TableCell className="capitalize">{contribution.payment_method}</TableCell>
                      <TableCell>
                        <Badge variant={contribution.payment_status === 'completed' ? 'default' : 'secondary'}>
                          {contribution.payment_status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      <RecordContributionDialog open={showDialog} onOpenChange={setShowDialog} />
    </DashboardLayout>
  );
}
